import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useApp } from "@/context/AppContext";
import { t, formatMoney } from "@/i18n";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { FileDropzone, AuthImage } from "@/components/FileTools";
import { BankDetails, EscrowLedgerButton, DirectContractButton } from "@/components/EscrowTools";
import { WireQRButton, ContractPdfButton, MonthlyReports } from "@/components/ReportsTools";
import { MandatesPanel } from "@/components/MandatesPanel";
import NDNCADialog from "@/components/NDNCADialog";
import EditPropertyDialog from "@/components/EditPropertyDialog";
import { PROPERTY_TYPES, propertyTypeLabel } from "@/lib/propertyTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil } from "lucide-react";
import ContactMatchButton from "@/components/ContactMatchButton";
import IntermediationInbox from "@/components/IntermediationInbox";
import Marketplace from "@/components/Marketplace";
import AdminPanel from "@/components/AdminPanel";
import MyListings from "@/components/MyListings";
import UnifiedListingDialog from "@/components/UnifiedListingDialog";
import NotificationsInbox from "@/components/NotificationsInbox";
import DocumentVault from "@/components/DocumentVault";
import ProfileEditDialog from "@/components/ProfileEditDialog";
import LegalFooter from "@/components/LegalFooter";
import useUnreadNotifications from "@/hooks/useUnreadNotifications";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import { Building2, FileSignature, Handshake, ShieldCheck, Lock, Plus, LayoutDashboard, Users as UsersIcon, TrendingUp, Vault, Bell, Clock, UserCog, Folder } from "lucide-react";

function KpiCard({ label, value, icon: Icon, testId }) {
  return (
    <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-6 rounded-lg" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs tracking-[0.2em] uppercase text-[#C5A880]">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-[#9A8B72]" />}
      </div>
      <p className="text-3xl text-[#1A1610] font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>{value}</p>
    </Card>
  );
}

function RoleSelection() {
  const { lang, refreshUser } = useApp();
  const setRole = async (role) => {
    try {
      await api.post("/auth/set-role", { role });
      await refreshUser();
      toast.success("Role updated");
    } catch (e) { toast.error("Error"); }
  };
  return (
    <div className="max-w-5xl mx-auto py-16">
      <p className="text-xs tracking-[0.4em] uppercase text-[#C5A880] mb-4">Onboarding</p>
      <h2 className="text-4xl font-light mb-10" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("role_choose", lang)}</h2>
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-8 hover:border-[#C5A880] transition-colors duration-200 cursor-pointer group" onClick={() => setRole("agent")} data-testid="select-role-agent">
          <FileSignature className="h-6 w-6 text-[#C5A880] mb-6" />
          <h3 className="text-2xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("role_agent", lang)}</h3>
          <p className="text-sm text-[#6B5F4C]">{t("role_agent_desc", lang)}</p>
        </Card>
        <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-8 hover:border-[#C5A880] transition-colors duration-200 cursor-pointer group" onClick={() => setRole("owner")} data-testid="select-role-owner">
          <Building2 className="h-6 w-6 text-[#C5A880] mb-6" />
          <h3 className="text-2xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("role_owner", lang)}</h3>
          <p className="text-sm text-[#6B5F4C]">{t("role_owner_desc", lang)}</p>
        </Card>
        <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-8 hover:border-[#C5A880] transition-colors duration-200 cursor-pointer group" onClick={() => setRole("buyer")} data-testid="select-role-buyer">
          <Handshake className="h-6 w-6 text-[#C5A880] mb-6" />
          <h3 className="text-2xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("role_buyer", lang)}</h3>
          <p className="text-sm text-[#6B5F4C]">{t("role_buyer_desc", lang)}</p>
        </Card>
      </div>
    </div>
  );
}

function PropertyForm({ onCreated }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", location: "", city: "", country: "", price: "", bedrooms: "", bathrooms: "", area_sqm: "", commission_pct: "3", property_type: "villa" });
  const [images, setImages] = useState([]);
  const [mandateFile, setMandateFile] = useState(null);
  const [matches, setMatches] = useState(null); // {property, matches[]}

  const submit = async () => {
    try {
      const body = {
        ...form,
        price: parseFloat(form.price) || 0,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        area_sqm: parseFloat(form.area_sqm) || 0,
        commission_pct: parseFloat(form.commission_pct) || 3,
      };
      const { data } = await api.post("/properties", body);
      const prop = data.property || data;
      for (const img of images) {
        const fd = new FormData();
        fd.append("file_id", img.file_id);
        await api.post(`/properties/${prop.id}/images`, fd);
      }
      if (mandateFile) {
        const fd = new FormData();
        fd.append("file_id", mandateFile.file_id);
        await api.post(`/properties/${prop.id}/mandate`, fd);
      }
      const matchCount = data.match_count ?? (data.matches?.length || 0);
      if (matchCount > 0) {
        toast.success(`Property created · ${matchCount} match!`);
        setMatches({ property: prop, matches: data.matches || [] });
        // Keep dialog open to show matches
      } else {
        toast.success("Property created");
        setOpen(false);
        setForm({ title: "", description: "", location: "", city: "", country: "", price: "", bedrooms: "", bathrooms: "", area_sqm: "", commission_pct: "3", property_type: "villa" });
        setImages([]); setMandateFile(null);
      }
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const closeAll = () => {
    setOpen(false);
    setMatches(null);
    setForm({ title: "", description: "", location: "", city: "", country: "", price: "", bedrooms: "", bathrooms: "", area_sqm: "", commission_pct: "3", property_type: "villa" });
    setImages([]); setMandateFile(null);
  };

  const inputCls = "bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-property-button" className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full">
          <Plus className="me-2 h-4 w-4" />
          {t("add_property", lang)}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle style={{ fontFamily: "Cormorant Garamond, serif" }} className="text-2xl font-light">{t("add_property", lang)}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <Label className="text-[#4A3F30]">Tipo di immobile</Label>
            <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
              <SelectTrigger data-testid="prop-type-input" className={inputCls}>
                <SelectValue placeholder="Seleziona il tipo…" />
              </SelectTrigger>
              <SelectContent>
                {PROPERTY_TYPES.map((pt) => (
                  <SelectItem key={pt.value} value={pt.value}>{pt[lang] || pt.it}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[#4A3F30]">{t("title", lang)}</Label><Input data-testid="prop-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><Label className="text-[#4A3F30]">{t("description", lang)}</Label><Textarea data-testid="prop-desc-input" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-[#4A3F30]">{t("city", lang)}</Label><Input data-testid="prop-city-input" className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label className="text-[#4A3F30]">{t("country", lang)}</Label><Input data-testid="prop-country-input" className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
          </div>
          <div><Label className="text-[#4A3F30]">{t("location", lang)}</Label><Input data-testid="prop-location-input" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label className="text-[#4A3F30]">{t("price", lang)}</Label><Input data-testid="prop-price-input" className={inputCls} type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div><Label className="text-[#4A3F30]">{t("bedrooms", lang)}</Label><Input className={inputCls} type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} /></div>
            <div><Label className="text-[#4A3F30]">{t("bathrooms", lang)}</Label><Input className={inputCls} type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></div>
            <div><Label className="text-[#4A3F30]">{t("area", lang)}</Label><Input className={inputCls} type="number" value={form.area_sqm} onChange={(e) => setForm({ ...form, area_sqm: e.target.value })} /></div>
          </div>
          <div><Label className="text-[#4A3F30]">{t("commission", lang)}</Label><Input data-testid="prop-commission-input" className={inputCls} type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} /></div>

          <div>
            <Label className="text-[#4A3F30] mb-2 block">{t("upload_images", lang)}</Label>
            <FileDropzone kind="image" accept="image/*" label={t("drop_here", lang)} testId="prop-images-drop"
              onUploaded={(f) => setImages((prev) => [...prev, f])} />
            {images.length > 0 && <p className="text-xs text-[#7A6E5A] mt-2">{images.length} file</p>}
          </div>
          <div>
            <Label className="text-[#4A3F30] mb-2 block">{t("upload_mandate", lang)}</Label>
            <FileDropzone kind="mandate" accept="application/pdf" label={t("drop_here", lang)} testId="prop-mandate-drop"
              onUploaded={(f) => setMandateFile(f)} />
            {mandateFile && <p className="text-xs text-[#7A6E5A] mt-2">{mandateFile.storage_path}</p>}
          </div>
        </div>
        <DialogFooter>
          {matches ? (
            <div className="w-full space-y-3" data-testid="matches-panel">
              <div className="rounded-md border-2 border-[#C5A880] bg-gradient-to-r from-[#F0E7D3] to-[#FFFAF0] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
                    {lang === "it" ? "Match trovati" : "Matches found"}
                  </span>
                  <span className="text-xs font-mono bg-[#C5A880] text-black rounded-full px-2 py-0.5" data-testid="matches-count">
                    {matches.matches.length}
                  </span>
                </div>
                {matches.matches.length === 0 ? (
                  <p className="text-sm text-[#6B5F4C]">{lang === "it" ? "Nessuna ricerca attiva matcha ancora questo immobile." : "No active searches match this property yet."}</p>
                ) : (
                  <ul className="space-y-2 max-h-56 overflow-y-auto">
                    {matches.matches.map((m) => (
                      <li key={m.id} data-testid={`match-item-${m.id}`} className="flex items-center justify-between gap-3 p-2 rounded bg-[#FFFAF0] border border-[#D8CFBB]">
                        <div className="min-w-0">
                          <p className="text-sm text-[#1A1610] font-medium truncate">{m.signer_name || "—"}</p>
                          <p className="text-xs text-[#6B5F4C] truncate">{m.search_area || "—"} · {m.search_budget ? `€ ${Number(m.search_budget).toLocaleString()}` : "—"}</p>
                        </div>
                        <ContactMatchButton mandateId={m.id} propertyId={matches.property.id} />
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-[#7A6E5A] mt-3">
                  {lang === "it" ? "Notifiche PEC inviate automaticamente ai buyer corrispondenti." : "PEC notifications sent to matching buyers."}
                </p>
              </div>
              <div className="flex justify-end">
                <Button onClick={closeAll} data-testid="matches-close-btn" className="bg-[#1A1610] text-[#F5F0E6] hover:bg-[#2E2A22] rounded-full">
                  {lang === "it" ? "Chiudi" : "Close"}
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={submit} data-testid="prop-submit-button" className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full">{t("submit", lang)}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DealForm({ property, onCreated }) {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [price, setPrice] = useState(property?.price || "");
  const [directFee, setDirectFee] = useState("");
  const [isDirect, setIsDirect] = useState(false);

  const submit = async () => {
    try {
      await api.post("/deals", {
        property_id: property.id,
        buyer_name: buyerName,
        sale_price: parseFloat(price) || 0,
        direct_fee: parseFloat(directFee) || 0,
        is_direct: isDirect,
      });
      toast.success("Deal created");
      setOpen(false); setBuyerName(""); setPrice(property.price); setDirectFee(""); setIsDirect(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const inputCls = "bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`new-deal-btn-${property.id}`} className="border-[#C5A880]/50 text-[#C5A880] bg-transparent hover:bg-[#C5A880]/10 rounded-full">
          {t("deal_new", lang)}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610]">
        <DialogHeader><DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("deal_new", lang)}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-4">
          <div><Label className="text-[#4A3F30]">{t("buyer", lang)}</Label><Input data-testid="deal-buyer-input" className={inputCls} value={buyerName} onChange={(e) => setBuyerName(e.target.value)} /></div>
          <div><Label className="text-[#4A3F30]">{t("sale_price", lang)}</Label><Input data-testid="deal-price-input" className={inputCls} type="number" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
          <div className="flex items-center gap-3 pt-2">
            <Checkbox id="is-direct" checked={isDirect} onCheckedChange={setIsDirect} data-testid="deal-is-direct" />
            <Label htmlFor="is-direct" className="text-[#2E2A22] cursor-pointer text-sm">
              {t("deal_direct", lang)}
            </Label>
          </div>
          <div>
            <Label className="text-[#4A3F30]">
              {t("deal_direct_fee_label", lang)}
            </Label>
            <Input data-testid="deal-direct-fee-input" className={inputCls} type="number" value={directFee} onChange={(e) => setDirectFee(e.target.value)} placeholder="0" />
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="deal-submit-button" className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full">{t("submit", lang)}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgentDocuments() {
  const { lang, user, refreshUser } = useApp();
  const [docs, setDocs] = useState([]);
  const [ndncaOpen, setNdncaOpen] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/agent/documents");
    setDocs(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async (docType, fileId) => {
    const fd = new FormData();
    fd.append("doc_type", docType); fd.append("file_id", fileId);
    await api.post("/agent/documents", fd);
    toast.success("Document uploaded");
    await load(); await refreshUser();
  };
  const has = (type) => docs.some((d) => d.doc_type === type);
  return (
    <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-8">
      <NDNCADialog open={ndncaOpen} setOpen={setNdncaOpen} onSigned={refreshUser} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A880] mb-2">{t("documents", lang)}</p>
          <h3 className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>NDNCA · Mandate · Commission</h3>
        </div>
        <Badge className={user.agent_approved ? "bg-emerald-600" : "bg-amber-600"} data-testid="agent-status-badge">
          {user.agent_approved ? t("approved", lang) : t("pending_approval", lang)}
        </Badge>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {/* NDNCA — OTP sign flow (uniform UX) */}
        <div className="border border-[#D8CFBB] rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-widest uppercase text-[#6B5F4C]">NDNCA</span>
            {user.ndnca_signed && <ShieldCheck className="h-4 w-4 text-emerald-600" />}
          </div>
          {user.ndnca_signed ? (
            <div className="text-center py-4">
              <p className="text-sm text-emerald-700 mb-3">
                {lang === "it" ? "NDNCA firmato con OTP" : "NDNCA signed via OTP"}
              </p>
              <Button
                size="sm"
                onClick={async () => {
                  const r = await api.get("/ndnca/pdf", { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a"); a.href = url; a.download = "ndnca.pdf"; a.click();
                  URL.revokeObjectURL(url);
                }}
                data-testid="agent-ndnca-pdf"
                className="bg-transparent border border-[#C5A880]/50 text-[#C5A880] hover:bg-[#C5A880]/10 rounded-full"
              >
                PDF
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setNdncaOpen(true)}
              data-testid="agent-sign-ndnca-otp"
              className="w-full bg-[#1A1610] text-[#F5F0E6] hover:bg-[#2E2A22] rounded-full"
            >
              <FileSignature className="h-4 w-4 me-2" />
              {lang === "it" ? "Firma con OTP" : "Sign with OTP"}
            </Button>
          )}
        </div>
        {/* Commission contract + Mandate — upload retained for external agents' own paperwork */}
        {[
          { type: "commission_contract", label: t("upload_commission", lang), testId: "upload-commission-drop" },
          { type: "mandate", label: t("upload_mandate", lang), testId: "upload-mandate-drop" },
        ].map((d) => (
          <div key={d.type} className="border border-[#D8CFBB] rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs tracking-widest uppercase text-[#6B5F4C]">{d.type.replace("_", " ")}</span>
              {has(d.type) && <ShieldCheck className="h-4 w-4 text-emerald-600" />}
            </div>
            <FileDropzone kind={d.type} accept="application/pdf" label={d.label} testId={d.testId}
              onUploaded={(f) => upload(d.type, f.file_id)} />
          </div>
        ))}
      </div>
    </Card>
  );
}

function NDNCAModal({ open, setOpen }) {
  const { refreshUser } = useApp();
  return <NDNCADialog open={open} setOpen={setOpen} onSigned={refreshUser} />;
}

function PropertyCard({ p, isAgent, onDeal, onRefresh }) {
  const { lang, user } = useApp();
  const locked = p.locked;
  const isAdmin = user?.role === "admin";
  const [editOpen, setEditOpen] = useState(false);
  return (
    <Card className="bg-[#FFFAF0] border-[#D8CFBB] overflow-hidden rounded-lg group hover:border-[#C5A880] transition-colors duration-200" data-testid={`property-card-${p.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-[#EBE3D3]/40">
        {p.image_paths?.[0] && !locked ? (
          <AuthImage fileId={p.image_paths[0]} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#F0E7D3] to-[#F5F0E6]">
            {locked ? <Lock className="h-8 w-8 text-[#B8A98F]" /> : <Building2 className="h-8 w-8 text-[#C4B99E]" />}
          </div>
        )}
        <Badge className="absolute top-3 start-3 bg-[#C5A880] text-black text-[10px] tracking-widest">{t("off_market", lang)}</Badge>
        {isAdmin && (
          <Button
            size="sm"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditOpen(true); }}
            data-testid={`admin-property-edit-${p.id}`}
            title="Modifica (admin)"
            className="absolute top-3 end-3 h-8 w-8 p-0 rounded-full bg-white/95 hover:bg-white text-[#B8965A] shadow-md"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="p-5">
        {p.property_type && (
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-2" data-testid={`property-type-${p.id}`}>
            {propertyTypeLabel(p.property_type, lang)}
          </p>
        )}
        <h4 className="text-lg font-light mb-1" style={{ fontFamily: "Cormorant Garamond, serif" }}>{p.title}</h4>
        <p className="text-xs text-[#7A6E5A] mb-3">{p.city}, {p.country}</p>
        <div className="flex items-center justify-between">
          <p className="text-lg text-[#C5A880]">{formatMoney(p.price, lang)}</p>
          {isAgent && <DealForm property={p} onCreated={onDeal} />}
        </div>
        {locked && <p className="text-xs text-amber-500 mt-3">{t("locked", lang)}</p>}
      </div>
      {isAdmin && (
        <EditPropertyDialog
          property={editOpen ? p : null}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onRefresh?.(); }}
        />
      )}
    </Card>
  );
}

function EscrowActions({ deal, onChange }) {
  const { lang, user } = useApp();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(deal.sale_price);
  const [ref, setRef] = useState("");

  const doDeposit = async () => {
    await api.post(`/deals/${deal.id}/escrow/deposit`, { amount: parseFloat(amount) || 0, reference: ref });
    toast.success(t("escrow_held", lang));
    setOpen(false);
    onChange?.();
  };
  const doRelease = async () => {
    await api.post(`/deals/${deal.id}/escrow/release`);
    toast.success(t("escrow_released", lang));
    onChange?.();
  };
  const doRefund = async () => {
    await api.post(`/deals/${deal.id}/escrow/refund`);
    toast.success(t("escrow_refunded", lang));
    onChange?.();
  };

  if (user.role !== "admin") return null;
  const inputCls = "bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]";

  if (deal.escrow_status === "awaiting_deposit" || deal.escrow_status === "none") {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" data-testid={`escrow-deposit-${deal.id}`} className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full">
            {t("escrow_deposit_btn", lang)}
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610]">
          <DialogHeader><DialogTitle className="text-xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("escrow", lang)}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div><Label className="text-[#4A3F30]">{t("escrow_amount", lang)}</Label><Input data-testid="escrow-amount-input" className={inputCls} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div><Label className="text-[#4A3F30]">{t("escrow_reference", lang)}</Label><Input data-testid="escrow-ref-input" className={inputCls} value={ref} onChange={(e) => setRef(e.target.value)} /></div>
            <BankDetails compact />
            <div className="flex justify-center pt-2"><WireQRButton deal={deal} /></div>
          </div>
          <DialogFooter><Button onClick={doDeposit} data-testid="escrow-deposit-submit" className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full">{t("submit", lang)}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  if (deal.escrow_status === "held") {
    return (
      <div className="flex gap-2">
        <Button size="sm" onClick={doRelease} data-testid={`escrow-release-${deal.id}`} className="bg-emerald-600 text-[#1A1610] hover:bg-emerald-500 rounded-full">
          {t("escrow_release_btn", lang)}
        </Button>
        <Button size="sm" variant="outline" onClick={doRefund} data-testid={`escrow-refund-${deal.id}`} className="border-red-600/60 text-red-400 bg-transparent hover:bg-red-950 rounded-full">
          {t("escrow_refund_btn", lang)}
        </Button>
      </div>
    );
  }
  return null;
}

function escrowBadgeCls(st) {
  switch (st) {
    case "held": return "bg-amber-600 text-black";
    case "released": return "bg-emerald-600 text-[#1A1610]";
    case "refunded": return "bg-red-700 text-[#1A1610]";
    case "awaiting_deposit": return "bg-[#EBE3D3]/60 text-[#1A1610]";
    default: return "bg-[#EBE3D3]/40 text-[#6B5F4C]";
  }
}

function escrowLabel(st, lang) {
  return {
    awaiting_deposit: t("escrow_awaiting", lang),
    held: t("escrow_held", lang),
    released: t("escrow_released", lang),
    refunded: t("escrow_refunded", lang),
    none: "—",
  }[st] || st;
}

function DealsTable({ deals, onChange }) {
  const { lang, user } = useApp();
  if (!deals.length) return <p className="text-sm text-[#7A6E5A] py-8 text-center">{t("no_data", lang)}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#D8CFBB] text-[#7A6E5A] text-xs uppercase tracking-widest">
            <th className="text-start p-3">{t("properties", lang)}</th>
            <th className="text-start p-3">{t("buyer", lang)}</th>
            <th className="text-start p-3">{t("sale_price", lang)}</th>
            <th className="text-start p-3">{t("agent_commission", lang)}</th>
            <th className="text-start p-3">{t("platform_fee", lang)}</th>
            <th className="text-start p-3">Direct fee</th>
            <th className="text-start p-3">{t("agent_net", lang)}</th>
            <th className="text-start p-3">{t("escrow_status", lang)}</th>
            <th className="text-start p-3">{t("direct_contract", lang)} / {t("escrow_ledger", lang)}</th>
            {user.role === "admin" && <th className="text-start p-3">{t("escrow", lang)}</th>}
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => (
            <tr key={d.id} className="border-b border-[#E5DDC9] hover:bg-[#EBE3D3] transition-colors duration-200" data-testid={`deal-row-${d.id}`}>
              <td className="p-3">{d.property_title}</td>
              <td className="p-3">{d.buyer_name}</td>
              <td className="p-3">{formatMoney(d.sale_price, lang)}</td>
              <td className="p-3">{formatMoney(d.agent_commission, lang)}</td>
              <td className="p-3 text-[#C5A880]">{formatMoney(d.platform_fee, lang)}</td>
              <td className="p-3">{d.direct_fee ? formatMoney(d.direct_fee, lang) : "—"}</td>
              <td className="p-3">{formatMoney(d.agent_net, lang)}</td>
              <td className="p-3">
                <Badge className={escrowBadgeCls(d.escrow_status)}>{escrowLabel(d.escrow_status, lang)}</Badge>
              </td>
              <td className="p-3">
                <div className="flex flex-wrap gap-2">
                  <EscrowLedgerButton deal={d} />
                  <DirectContractButton deal={d} onSigned={onChange} />
                  <ContractPdfButton deal={d} />
                  <WireQRButton deal={d} />
                </div>
              </td>
              {user.role === "admin" && (
                <td className="p-3"><EscrowActions deal={d} onChange={onChange} /></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading, lang } = useApp();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [ndncaOpen, setNdncaOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("marketplace");
  useEffect(() => {
    const handler = () => setActiveTab("searches");
    window.addEventListener("open-mandate-form", handler);
    return () => window.removeEventListener("open-mandate-form", handler);
  }, []);
  const { count: unreadCount, refresh: refreshUnread, markAllRead } = useUnreadNotifications({ enabled: !!user, showToasts: false });

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [pr, dl] = await Promise.all([api.get("/properties"), api.get("/deals").catch(() => ({ data: [] }))]);
      setProperties(pr.data); setDeals(dl.data);
      if (user.role === "admin") {
        const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
        setStats(s.data); setUsers(u.data);
      }
    } catch (e) { logError("Dashboard load failed", e); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen bg-[#F5F0E6] text-[#1A1610] flex items-center justify-center">{t("loading", lang)}</div>;
  if (!user) return null;

  const needsRole = !user.role || (user.role === "buyer" && !localStorage.getItem("aaron_role_confirmed_" + user.user_id));

  const approveAgent = async (uid) => {
    await api.post(`/admin/users/${uid}/approve`);
    toast.success("Agent approved");
    load();
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6] text-[#1A1610]">
      <Header />
      <NDNCAModal open={ndncaOpen} setOpen={setNdncaOpen} />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <p className="text-xs tracking-[0.4em] uppercase text-[#C5A880] mb-3">{t("dashboard", lang)}</p>
          <h1 className="text-4xl sm:text-5xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }} data-testid="dashboard-title">
            {t("dash_welcome", lang)}, {user.name}
          </h1>
        </div>

        {!user.ndnca_signed && user.role !== "admin" && (
          <div
            className="mb-8 rounded-lg border-2 border-[#C5A880] bg-gradient-to-r from-[#F4EFE6] to-[#FFFAF0] p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            data-testid="ndnca-reminder-widget"
          >
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-[#C5A880] flex items-center justify-center shrink-0">
                <Lock className="h-5 w-5 text-black" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-1">
                  {t("required_step", lang)}
                </p>
                <h3 className="text-xl font-light text-[#1A1610] mb-1" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  {t("ndnca_widget_headline", lang)}
                </h3>
                <p className="text-xs text-[#4A3F30] max-w-2xl">
                  {t("ndnca_widget_sub", lang)}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setNdncaOpen(true)}
              data-testid="ndnca-widget-cta"
              className="bg-[#1A1610] text-[#F5F0E6] hover:bg-[#2E2A22] rounded-full px-6 h-11 shrink-0"
            >
              <FileSignature className="h-4 w-4 me-2" />
              {t("sign_ndnca_now", lang)}
            </Button>
          </div>
        )}

        {user.role === "admin" && stats && (
          <div className="mb-10 grid grid-cols-2 md:grid-cols-5 gap-4">
            <KpiCard testId="admin-kpi-users" label={t("admin_users", lang)} value={stats.total_users} icon={UsersIcon} />
            <KpiCard testId="admin-kpi-properties" label={t("properties", lang)} value={stats.total_properties} icon={Building2} />
            <KpiCard testId="admin-kpi-deals" label={t("deals", lang)} value={stats.total_deals} icon={Handshake} />
            <KpiCard testId="admin-kpi-revenue" label={t("admin_revenue", lang)} value={formatMoney((stats.total_platform_fee || 0) + (stats.total_direct_fee || 0), lang)} icon={TrendingUp} />
            <KpiCard testId="admin-kpi-escrow" label={t("escrow_held_kpi", lang)} value={formatMoney(stats.escrow_held || 0, lang)} icon={Vault} />
          </div>
        )}

        {(() => {
          // Gate the marketplace + tabs behind NDNCA + admin approval.
          const canAccessBoard = user.role === "admin" || (user.ndnca_signed && user.agent_approved);
          if (canAccessBoard) return null;
          return (
            <div
              className="mb-10 rounded-lg border border-[#D8CFBB] bg-[#FFFAF0] p-8 text-center"
              data-testid="awaiting-approval-widget"
            >
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-[#F5EDDA] flex items-center justify-center">
                <Clock className="h-5 w-5 text-[#B8965A]" />
              </div>
              <h3
                className="text-2xl font-light text-[#1A1610] mb-2"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {t("awaiting_approval_title", lang)}
              </h3>
              <p className="text-sm text-[#4A3F30] max-w-xl mx-auto">
                {user.ndnca_signed
                  ? t("awaiting_approval_body", lang)
                  : t("ndnca_first_needed", lang)}
              </p>
            </div>
          );
        })()}

        {(user.role === "admin" || (user.ndnca_signed && user.agent_approved)) && (
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            setActiveTab(v);
            if (v === "notifications" && unreadCount > 0) { markAllRead(); }
          }}
          className="w-full"
        >
          <TabsList className="bg-[#FFFAF0] border border-[#D8CFBB] rounded-full p-1 mb-8 flex-wrap h-auto">
            <TabsTrigger value="marketplace" data-testid="tab-marketplace" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
              {t("board_tab", lang)}
            </TabsTrigger>
            <TabsTrigger value="properties" data-testid="tab-properties" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
              {t("properties", lang)}
            </TabsTrigger>
            {(["agent","owner","admin"].includes(user.role)) && (
              <TabsTrigger value="deals" data-testid="tab-deals" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
                {t("deals", lang)}
              </TabsTrigger>
            )}
            {(["agent","owner"].includes(user.role)) && (
              <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
                {t("documents", lang)}
              </TabsTrigger>
            )}
            {user.role === "admin" && (
              <TabsTrigger value="admin" data-testid="tab-admin" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
                <LayoutDashboard className="me-2 h-3.5 w-3.5" /> Admin
              </TabsTrigger>
            )}
            {user.role === "admin" && (
              <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
                Reports
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" data-testid="tab-notifications" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black relative">
              <Bell className="me-2 h-3.5 w-3.5" /> {t("notifications", lang)}
              {unreadCount > 0 && (
                <span
                  data-testid="notifications-unread-badge"
                  className="ms-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </TabsTrigger>
            {(["owner","buyer","agent","admin"].includes(user.role)) && (
              <TabsTrigger value="searches" data-testid="tab-searches" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
                {t("searches_tab", lang)}
              </TabsTrigger>
            )}
            <TabsTrigger value="role" data-testid="tab-role" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
              Role
            </TabsTrigger>
            <TabsTrigger value="my-docs" data-testid="tab-my-docs" className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black">
              <Folder className="me-2 h-3.5 w-3.5" /> {t("my_documents_tab", lang)}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="marketplace">
            <Marketplace />
          </TabsContent>

          <TabsContent value="properties">
            {user.role === "buyer" && !user.ndnca_signed && (
              <Card className="bg-amber-950/30 border-amber-800/50 p-5 mb-6 flex items-center justify-between" data-testid="ndnca-alert">
                <div className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-amber-500" />
                  <p className="text-sm text-amber-100">{t("locked", lang)}</p>
                </div>
                <Button onClick={() => setNdncaOpen(true)} data-testid="open-ndnca-button" className="bg-[#B8965A] text-white hover:bg-[#A0824D] rounded-full">
                  {t("sign_ndnca_now", lang)}
                </Button>
              </Card>
            )}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("properties", lang)}</h2>
              {(["agent","owner","buyer","admin"].includes(user.role)) && <UnifiedListingDialog onCreated={load} />}
            </div>
            {(["agent","owner"].includes(user.role)) && (
              <div className="mb-6">
                <MyListings />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
              {properties.map((p) => (
                <PropertyCard key={p.id} p={p} isAgent={["agent","owner","admin"].includes(user.role)} onDeal={load} onRefresh={load} />
              ))}
              {!properties.length && <p className="text-sm text-[#7A6E5A] col-span-full py-10 text-center">{t("no_data", lang)}</p>}
            </div>
          </TabsContent>

          {(["agent","owner","admin"].includes(user.role)) && (
            <TabsContent value="deals">
              <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-6">
                <DealsTable deals={deals} onChange={load} />
              </Card>
            </TabsContent>
          )}

          {(["agent","owner"].includes(user.role)) && (
            <TabsContent value="documents">
              <AgentDocuments />
            </TabsContent>
          )}

          {user.role === "admin" && (
            <TabsContent value="admin">
              <div className="space-y-6">
                <AdminPanel />
                <Card className="bg-white border-[#E5E5E3] p-6">
                  <h3 className="text-2xl font-light mb-6 text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{t("admin_users", lang)}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-[#D8CFBB] text-[#7A6E5A] text-xs uppercase tracking-widest">
                      <th className="text-start p-3">Name</th><th className="text-start p-3">Email</th><th className="text-start p-3">Role</th><th className="text-start p-3">NDNCA</th><th className="text-start p-3">Approved</th><th className="text-start p-3"></th>
                    </tr></thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-[#E5DDC9]" data-testid={`admin-user-${u.user_id}`}>
                          <td className="p-3">{u.name}</td>
                          <td className="p-3 text-[#6B5F4C]">{u.email}</td>
                          <td className="p-3"><Badge className="bg-[#EBE3D3]/60 text-[#1A1610]">{u.role}</Badge></td>
                          <td className="p-3">{u.ndnca_signed ? "✓" : "—"}</td>
                          <td className="p-3">{u.agent_approved ? <Badge className="bg-emerald-600">yes</Badge> : <Badge className="bg-amber-600">no</Badge>}</td>
                          <td className="p-3">
                            {u.role === "agent" && !u.agent_approved && (
                              <Button size="sm" onClick={() => approveAgent(u.user_id)} data-testid={`approve-${u.user_id}`} className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full">{t("approve", lang)}</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </Card>
              </div>
            </TabsContent>
          )}

          <TabsContent value="notifications">
            <div className="space-y-6">
              <IntermediationInbox />
              <NotificationsInbox />
            </div>
          </TabsContent>

          {(["owner","buyer","agent","admin"].includes(user.role)) && (
            <TabsContent value="searches">
              <MandatesPanel defaultType={user.role === "owner" ? "sale" : "search"} />
            </TabsContent>
          )}

          {user.role === "admin" && (
            <TabsContent value="reports">
              <MonthlyReports />
            </TabsContent>
          )}

          <TabsContent value="role">
            <RoleSelection />
          </TabsContent>

          <TabsContent value="my-docs">
            <div className="space-y-6" data-testid="my-docs-panel">
              <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-1">
                    {t("profile_section", lang)}
                  </p>
                  <h3 className="text-2xl font-light text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                    {user.name}
                  </h3>
                  <p className="text-xs text-[#7A6E5A] font-mono mt-1">{user.email}</p>
                </div>
                <ProfileEditDialog />
              </Card>
              <DocumentVault />
            </div>
          </TabsContent>
        </Tabs>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}
